import type {
  EnvironmentData,
  EventData,
  PlayRecord,
  SnapshotData,
  TuningState,
} from "@slime/contracts";
import {
  DEFAULT_TELEMETRY_POLICY,
  type TelemetryPolicy,
} from "@slime/contracts/projects";
import type { GameContext } from "../engine/core/GameContext";
import { EventKind } from "../engine/events/EventQueue";
import { RecordAggregator } from "./RecordAggregator";
import { captureScene } from "./captureScene";
import type { PressureState } from "../mechanics/pressure/PressureState";
import { RecordTransport } from "./RecordTransport";

interface Attempt {
  id: string;
  levelId: string;
  start: number;
  ended: boolean;
}
const STAGED_CAPACITY = 8192;
export class GameRecorder {
  readonly transport: RecordTransport;
  /** 从记录流算出的诊断指标（决策 0017 第八条）：面板与埋点共用，不另开旁路统计。 */
  readonly aggregator = new RecordAggregator();
  private readonly start = performance.now();
  private sequence = 0;
  private attempt?: Attempt;
  private closed = false;
  private feedbackSubmitted = false;
  private disabled = false;
  /** 采集是否已因故障停止。诊断用，只读。 */
  get isDisabled(): boolean {
    return this.disabled;
  }
  private nextSnapshot = 0;
  private readonly identities = new Map<number, number>();
  private dropped: [number, number][] = [];
  private untrackedDrops = false;
  private readonly inputMethods = new Set<
    EnvironmentData["inputMethods"][number]
  >();
  private readonly cleanup: (() => void)[] = [];
  readonly diagnostics = { captureFailures: 0, maxCaptureMs: 0, stagedPeak: 0 };
  private pixelRatio = window.devicePixelRatio;
  private environmentDirty = true;
  private envDue = 0;
  // Flat staging buffer copies ephemeral engine events without per-event payload allocation.
  private count = 0;
  private head = 0;
  private readonly staged = new Float64Array(STAGED_CAPACITY * 10);
  private readonly owners: (Attempt | undefined)[] = new Array(STAGED_CAPACITY);
  private timer?: ReturnType<typeof setInterval>;
  private pageHidden = document.hidden;
  private policy: TelemetryPolicy;
  private awaitingPolicy: boolean;
  constructor(
    private readonly context: GameContext,
    private readonly canvas: HTMLCanvasElement,
    endpoint: string,
    gameVersion: string,
    private readonly tuning: () => TuningState,
    projectId = "slime-defender",
    policy: TelemetryPolicy | undefined,
    private readonly pressure?: PressureState,
  ) {
    this.awaitingPolicy = policy === undefined;
    this.policy = policy ?? DEFAULT_TELEMETRY_POLICY;
    const snapshots = this.policy.mode === "full";
    this.transport = new RecordTransport(
      endpoint,
      {
        protocolVersion: 3,
        projectId,
        capabilities: { snapshots },
        presentation: snapshots
          ? {
              sceneVersion: 1,
              manifestSha256: __REPLAY_MANIFEST_HASH__,
            }
          : null,
        sessionId: crypto.randomUUID(),
        gameVersion,
        sessionStartedAt: new Date().toISOString(),
      },
      {
        onDrop: (seq) => this.noteDrop(seq),
        onStop: () => {
          this.disabled = true;
        },
        onAccept: (record) => this.aggregator.observe(record),
        minUploadIntervalMs: this.policy.minUploadIntervalMs,
        maxUploadIntervalMs: this.policy.maxUploadIntervalMs,
        flushThresholdBytes: this.policy.flushThresholdBytes,
        deferred: this.awaitingPolicy,
      },
    );
    this.transport.enqueue({
      ...this.base(undefined, ++this.sequence, this.start),
      kind: "event",
      data: {
        type: "system",
        name: "session_started",
        payload: {
          paused: false,
          visibility: document.hidden ? "hidden" : "visible",
        },
      },
    });
    void this.transport.flushCritical();
    if (snapshots) {
      this.transport.enqueue({
        ...this.base(undefined, ++this.sequence, this.start),
        kind: "snapshot",
        data: { reason: "start", ...captureScene(this.context, this.pressure) },
      });
      this.environment();
    }
    const listen = (
      target: EventTarget,
      name: string,
      callback: EventListener,
    ) => {
      target.addEventListener(name, callback);
      this.cleanup.push(() => target.removeEventListener(name, callback));
    };
    const dirty = () => {
      this.environmentDirty = true;
      this.envDue = performance.now() + 200;
    };
    if (snapshots) {
      listen(window, "resize", dirty);
      if (window.visualViewport) listen(window.visualViewport, "resize", dirty);
      const observer = new ResizeObserver(dirty);
      observer.observe(canvas);
      this.cleanup.push(() => observer.disconnect());
    }
    listen(document, "visibilitychange", () =>
      this.guard(() => {
        const state =
          document.visibilityState === "hidden" ? "hidden" : "visible";
        this.pageHidden = state === "hidden";
        this.event(
          { type: "system", name: "visibility_changed", payload: { state } },
          false,
        );
        this.drain();
        if (state === "hidden") void this.transport.flush(true);
        else this.nextSnapshot = 0;
      }),
    );
    listen(window, "pagehide", ((event: PageTransitionEvent) =>
      this.guard(() => {
        if (event.persisted) {
          this.drain();
          void this.transport.flush(true);
        } else this.close("normal");
      })) as EventListener);
    listen(window, "pageshow", () => {
      this.pageHidden = document.hidden;
      this.nextSnapshot = 0;
      dirty();
    });
    this.timer = setInterval(
      () =>
        this.guard(() => {
          this.drain();
          if (this.pixelRatio !== window.devicePixelRatio)
            this.environmentDirty = true;
          if (this.untrackedDrops && !this.closed) this.close("truncated");
          if (
            this.policy.mode === "full" &&
            this.environmentDirty &&
            performance.now() >= this.envDue &&
            !this.closed
          )
            this.environment();
          void this.transport.flush();
        }),
      250,
    );
  }
  /** Resolve a provisional in-memory recording against the authoritative project policy. */
  resolvePolicy(policy: TelemetryPolicy): boolean {
    if (!this.awaitingPolicy) return policy.mode !== "off";
    this.awaitingPolicy = false;
    if (policy.mode === "off") {
      this.discard();
      return false;
    }
    this.policy = policy;
    if (policy.mode === "level") {
      this.count = 0;
      this.head = 0;
      this.owners.fill(undefined);
      this.dropped = [];
      this.untrackedDrops = false;
      let sequence = 0;
      const retained = this.transport.rewriteBuffered((record) => {
        if (record.kind !== "event" || record.data.type !== "system")
          return undefined;
        const seq = ++sequence;
        if (record.data.name === "attempt_started") {
          const { snapshotRecordId: _snapshotRecordId, ...data } = record.data;
          return {
            ...record,
            seq,
            data: {
              ...data,
              payload: { ...data.payload, context: {} },
            },
          };
        }
        if (record.data.name === "attempt_ended") {
          const { snapshotRecordId: _snapshotRecordId, ...data } = record.data;
          return { ...record, seq, data };
        }
        if (record.data.name === "recording_closed")
          return {
            ...record,
            seq,
            data: {
              ...record.data,
              payload: { ...record.data.payload, finalSeq: seq, droppedRanges: [] },
            },
          };
        return { ...record, seq };
      });
      this.sequence = sequence;
      this.aggregator.reset();
      for (const record of retained) this.aggregator.observe(record);
    }
    this.nextSnapshot = performance.now() + policy.snapshotIntervalMs;
    this.transport.activate({
      snapshots: policy.mode === "full",
      presentation:
        policy.mode === "full"
          ? { sceneVersion: 1, manifestSha256: __REPLAY_MANIFEST_HASH__ }
          : null,
      minUploadIntervalMs: policy.minUploadIntervalMs,
      maxUploadIntervalMs: policy.maxUploadIntervalMs,
      flushThresholdBytes: policy.flushThresholdBytes,
    });
    void this.transport.flushCritical();
    return true;
  }
  /** Configuration failure/off means the provisional data was never telemetry. */
  discard(): void {
    this.closed = true;
    if (this.timer) clearInterval(this.timer);
    for (const clean of this.cleanup.splice(0)) clean();
    this.transport.abandon();
  }
  /** Fault isolation wraps recording only, never the game update itself. */
  guard(action: () => void): void {
    if (this.disabled) return;
    const start = performance.now();
    try {
      action();
    } catch {
      this.diagnostics.captureFailures++;
      this.disabled = true;
      if (this.timer) clearInterval(this.timer);
      console.warn("Play recording disabled after a capture error");
    } finally {
      this.diagnostics.maxCaptureMs = Math.max(
        this.diagnostics.maxCaptureMs,
        performance.now() - start,
      );
    }
  }
  private noteDrop(seq: number) {
    const tail = this.dropped.at(-1);
    if (tail && seq === tail[1] + 1) {
      tail[1] = seq;
      return;
    }
    if (this.dropped.some(([a, b]) => seq >= a && seq <= b)) return;
    this.dropped.push([seq, seq]);
    this.dropped.sort((a, b) => a[0] - b[0]);
    const ranges: [number, number][] = [];
    for (const range of this.dropped) {
      const last = ranges.at(-1);
      if (last && range[0] <= last[1] + 1)
        last[1] = Math.max(last[1], range[1]);
      else ranges.push(range);
    }
    this.dropped = ranges.slice(0, 256);
    if (ranges.length > 256) {
      this.untrackedDrops = true;
    }
  }
  private base(
    attempt: Attempt | undefined,
    seq = ++this.sequence,
    time = performance.now(),
  ) {
    return {
      recordId: crypto.randomUUID(),
      seq,
      sessionMs: Math.max(0, Math.floor(time - this.start)),
      scope: attempt
        ? {
            type: "attempt" as const,
            attemptId: attempt.id,
            unitId: attempt.levelId,
            startSessionMs: Math.round(attempt.start - this.start),
          }
        : { type: "session" as const },
    };
  }
  private event(data: EventData, scoped = true) {
    if (!this.closed)
      this.transport.enqueue({
        ...this.base(scoped ? this.attempt : undefined),
        kind: "event",
        data,
      });
  }
  begin(reason: "initial" | "retry" | "next"): void {
    this.guard(() => {
      if (this.closed) return;
      const previous = this.attempt;
      this.attempt = {
        id: crypto.randomUUID(),
        levelId: this.context.level.id,
        start: this.start + Math.floor(performance.now() - this.start),
        ended: false,
      };
      this.identities.clear();
      if (this.policy.mode === "full")
        for (let i = 0; i < this.context.slimes.capacity; i++)
          if (this.context.slimes.active[i])
            this.identities.set(
              this.context.slimes.recordingEntityId[i],
              this.context.slimes.definition[i],
            );
      const snapshotRecordId =
        this.policy.mode === "full" ? crypto.randomUUID() : undefined;
      this.transport.enqueue({
        ...this.base(this.attempt, ++this.sequence, this.attempt.start),
        kind: "event",
        data: {
          type: "system",
          name: "attempt_started",
          ...(snapshotRecordId ? { snapshotRecordId } : {}),
          payload: {
            reason,
            context:
              this.policy.mode === "full"
                ? {
                    tuning: { ...this.tuning() },
                    seed: this.context.state.seed,
                  }
                : {},
            ...(previous ? { previousAttemptId: previous.id } : {}),
          },
        },
      });
      void this.transport.flushCritical();
      if (snapshotRecordId) this.snapshot("start", snapshotRecordId);
      this.nextSnapshot = performance.now() + this.policy.snapshotIntervalMs;
      const phase = this.context.state.phase;
      if (
        phase === "game-over" ||
        phase === "level-clear" ||
        phase === "complete"
      )
        this.end(phase === "game-over" ? "failed" : "cleared", "outcome");
    });
  }
  end(
    outcome: "cleared" | "failed" | "aborted",
    reason: "outcome" | "retry" | "leave",
  ): void {
    this.guard(() => {
      if (!this.attempt || this.attempt.ended || this.closed) return;
      const id = this.policy.mode === "full" ? crypto.randomUUID() : undefined;
      this.event({
        type: "system",
        name: "attempt_ended",
        ...(id ? { snapshotRecordId: id } : {}),
        payload: {
          completion: outcome === "aborted" ? "aborted" : "finished",
          result:
            outcome === "cleared"
              ? "success"
              : outcome === "failed"
                ? "failure"
                : "none",
          reason,
          metrics: [],
        },
      });
      void this.transport.flushCritical();
      if (id) this.snapshot("keyframe", id);
      this.attempt.ended = true;
      if (this.policy.mode === "full") this.snapshot("keyframe");
    });
  }
  pause(paused: boolean) {
    this.guard(() => {
      this.event(
        { type: "system", name: paused ? "paused" : "resumed", payload: {} },
        false,
      );
      void this.transport.flushCritical();
      if (this.policy.mode === "full")
        this.snapshot("keyframe", undefined, true);
    });
  }
  submitRecommendation(rating: number): boolean {
    if (this.closed || this.disabled || this.feedbackSubmitted ||
        this.context.state.phase !== "complete" ||
        !Number.isInteger(rating) || rating < 1 || rating > 5) return false;
    this.guard(() => {
      const pending = this.transport.pendingCount;
      this.event({
        type: "system",
        name: "feedback_submitted",
        payload: { questionId: "game_enjoyment", rating },
      }, false);
      this.feedbackSubmitted = this.transport.pendingCount > pending;
      if (this.feedbackSubmitted) void this.transport.flushCritical();
    });
    return this.feedbackSubmitted;
  }
  /** 局中改参数是事实事件（决策 0017）：回放因此仍能解释「为什么同样的输入产生不同结果」。 */
  tuningChanged(path: string, from: number, to: number): void {
    if (this.policy.mode !== "full") return;
    this.guard(() => {
      if (this.attempt && !this.attempt.ended)
        this.event({
          type: "game",
          name: "tuning:changed",
          label: "参数变化",
          category: "diagnostic",
          entities: [],
          payload: { path, from, to, step: this.context.state.steps },
        });
    });
  }
  input(
    action: "fire_start" | "fire_stop" | "charge_start" | "charge_release",
    method?: string,
  ): void {
    if (this.policy.mode !== "full") return;
    this.guard(() => {
      if (method && ["mouse", "touch", "keyboard", "pen"].includes(method)) {
        const typed = method as EnvironmentData["inputMethods"][number];
        if (!this.inputMethods.has(typed)) {
          this.inputMethods.add(typed);
          this.environmentDirty = true;
        }
      }
      if (this.attempt && !this.attempt.ended)
        this.event({
          type: "game",
          name: "water:input",
          label: action,
          category: "input",
          entities: ["player"],
          payload: {
            action,
            x: this.context.input.pointerX,
            y: this.context.input.pointerY,
          },
        });
    });
  }
  collect(): void {
    this.guard(() => {
      if (!this.attempt || this.attempt.ended || this.closed) return;
      const events = this.context.events;
      if (this.policy.mode === "full") {
        const time = performance.now();
        for (let i = 0; i < events.count; i++) {
          const kind = events.kind[i];
          if (kind === EventKind.CollisionDetected) continue;
          if (kind === EventKind.LevelEnded) continue;
          const target = events.targetRecordingEntityId[i];
          const source = events.sourceRecordingEntityId[i];
          // 投放的史莱姆与发射的水滴共用 EntitySpawned：value 为 1 是史莱姆（定义下标直接作编码），否则是水滴（256 起）。
          if (kind === EventKind.EntitySpawned)
            this.identities.set(
              target,
              events.value[i] > 0
                ? events.definition[i]
                : 256 + events.definition[i],
            );
          const targetCode = this.identities.get(target) ?? -1;
          const sourceCode = this.identities.get(source) ?? -1;
          const seq = ++this.sequence;
          if (this.count >= STAGED_CAPACITY) this.noteDrop(seq);
          else {
            const slot = (this.head + this.count) % STAGED_CAPACITY;
            const offset = slot * 10;
            this.owners[slot] = this.attempt;
            this.staged[offset] = kind;
            this.staged[offset + 1] = target;
            this.staged[offset + 2] = source;
            this.staged[offset + 3] = targetCode;
            this.staged[offset + 4] = sourceCode;
            this.staged[offset + 5] = events.x[i];
            this.staged[offset + 6] = events.y[i];
            this.staged[offset + 7] = events.value[i];
            this.staged[offset + 8] = seq;
            this.staged[offset + 9] = time;
            this.count++;
            this.diagnostics.stagedPeak = Math.max(
              this.diagnostics.stagedPeak,
              this.count,
            );
          }
          if (kind === EventKind.EntityDespawned)
            this.identities.delete(target);
        }
      }
      for (let i = 0; i < events.count; i++)
        if (events.kind[i] === EventKind.LevelEnded)
          this.end(events.value[i] > 0 ? "cleared" : "failed", "outcome");
    });
  }
  private drain(limit = 100) {
    const length = Math.min(this.count, limit);
    for (let n = 0; n < length; n++) {
      const i = (this.head + n) % STAGED_CAPACITY;
      const o = i * 10,
        a = this.staged;
      const kind = a[o],
        target = a[o + 1],
        source = a[o + 2];
      const name =
        kind === EventKind.EntitySpawned
          ? "spawned"
          : kind === EventKind.EntityDied
            ? "died"
            : "despawned";
      const data: EventData = {
        type: "game",
        name: `entity:${name}`,
        label: name,
        category: "fact",
        entities: [String(target)],
        payload: {
          x: a[o + 5],
          y: a[o + 6],
          source: source ? String(source) : "player",
        },
      };
      this.transport.enqueue({
        ...this.base(this.owners[i], a[o + 8], a[o + 9]),
        kind: "event",
        data,
      });
      this.owners[i] = undefined;
    }
    this.head = (this.head + length) % STAGED_CAPACITY;
    this.count -= length;
  }
  sample() {
    this.guard(() => {
      if (
        !this.closed &&
        this.policy.mode === "full" &&
        !this.pageHidden &&
        performance.now() >= this.nextSnapshot
      ) {
        this.snapshot("periodic");
        this.nextSnapshot =
          performance.now() + this.policy.snapshotIntervalMs;
      }
    });
  }
  private snapshot(
    reason: SnapshotData["reason"],
    recordId?: string,
    session = false,
  ) {
    if (this.closed || this.policy.mode !== "full") return;
    const owner =
      !session && this.attempt && !this.attempt.ended
        ? this.attempt
        : undefined;
    const record: PlayRecord = {
      ...this.base(owner),
      ...(recordId ? { recordId } : {}),
      kind: "snapshot",
      data: { reason, ...captureScene(this.context, this.pressure) },
    };
    this.transport.enqueue(record);
  }
  private environment() {
    const rect = this.canvas.getBoundingClientRect();
    const viewport = window.visualViewport;
    const ua = navigator.userAgent;
    const osName = /Android/.test(ua)
      ? "Android"
      : /iPhone|iPad|iPod/.test(ua) ||
          (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
        ? "iOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Macintosh/.test(ua)
            ? "macOS"
            : /Linux/.test(ua)
              ? "Linux"
              : null;
    let browser: EnvironmentData["browser"] = { name: null, version: null };
    for (const [name, pattern] of [
      ["Edge", /Edg(?:A|iOS)?\/(\d+)/],
      ["Firefox", /(?:Firefox|FxiOS)\/(\d+)/],
      ["Chrome", /(?:Chrome|CriOS)\/(\d+)/],
      ["Safari", /Version\/(\d+).*Safari/],
    ] as const) {
      const match = ua.match(pattern);
      if (match) {
        browser = { name, version: match[1] };
        break;
      }
    }
    this.pixelRatio = window.devicePixelRatio;
    this.transport.enqueue({
      ...this.base(undefined),
      kind: "environment",
      data: {
        platform:
          osName === "Android" || osName === "iOS"
            ? "mobile_web"
            : osName
              ? "desktop_web"
              : "unknown",
        os: { name: osName, version: null },
        browser,
        screenCss: { width: screen.width, height: screen.height },
        viewportCss: {
          width: viewport?.width ?? window.innerWidth,
          height: viewport?.height ?? window.innerHeight,
        },
        gameRectCss: {
          x: rect.x - (viewport?.offsetLeft ?? 0),
          y: rect.y - (viewport?.offsetTop ?? 0),
          width: rect.width,
          height: rect.height,
        },
        devicePixelRatio: window.devicePixelRatio,
        canvasPixels: { width: this.canvas.width, height: this.canvas.height },
        inputMethods: [...this.inputMethods],
      },
    });
    this.environmentDirty = false;
  }
  close(mode: "normal" | "truncated") {
    if (this.closed) return;
    if (mode === "normal") this.end("aborted", "leave");
    this.drain(STAGED_CAPACITY);
    const seq = ++this.sequence;
    this.closed = true;
    this.transport.enqueue({
      ...this.base(undefined, seq),
      kind: "event",
      data: {
        type: "system",
        name: "recording_closed",
        payload: {
          mode: this.untrackedDrops ? "truncated" : mode,
          finalSeq: seq,
          droppedRanges: this.dropped.map(([a, b]) => [a, b]),
        },
      },
    });
    void this.transport.flushCritical();
  }
  destroy() {
    this.guard(() => this.close("truncated"));
    if (this.timer) clearInterval(this.timer);
    for (const clean of this.cleanup) clean();
  }
}
